<script lang="ts">
  /**
   * Persistent banner shown when a user has a server session but no DEK.
   *
   * Happens after every full-page reload (the DEK lives only in memory —
   * see lib/session.svelte.ts) and means private rows can't decrypt and
   * new private rows can't be created. Without this banner the user
   * encountered confusing failure modes (a "Logg inn på nytt" line under
   * each private row, a raw `not_logged_in` error on save).
   *
   * The form runs the regular login() flow under the hood — that
   * derives the KEK from the password and salt and unwraps the existing
   * wrapped_dek_pw, so private ciphertexts from before the reload stay
   * readable. The cookie is replaced as a side effect; that's fine.
   *
   * Hidden until both: user IS logged in, DEK IS missing. Renders
   * nothing otherwise — App.svelte mounts it unconditionally.
   */
  import { session } from '../lib/session.svelte';
  import { login } from '../lib/auth';
  import { ApiError } from '../lib/api';

  let password = $state('');
  let busy = $state(false);
  let error: string | null = $state(null);

  // Show the banner whenever the user is "logged in but locked." A
  // dismissed flag would only hide it temporarily — the next reload puts
  // them right back here, and the message is brief enough that it doesn't
  // pay to make it dismissable.
  const visible = $derived(!!session.user && !session.dek);

  async function submit(e: SubmitEvent) {
    e.preventDefault();
    if (!session.user || busy) return;
    error = null;
    busy = true;
    try {
      await login(session.user.email, password);
      password = '';
    } catch (err) {
      error = err instanceof ApiError && err.status === 401
        ? 'Feil passord. Prøv igjen.'
        : 'Klarte ikke å låse opp. Prøv igjen om litt.';
    } finally {
      busy = false;
    }
  }
</script>

{#if visible}
  <aside class="banner" role="region" aria-label="Lås opp privat innhold">
    <form onsubmit={submit} class="row" style="gap: 0.5rem; align-items: center; margin: 0;">
      <span style="flex: 1 1 auto;">
        🔒 Privat innhold er låst etter sideoppdatering.
      </span>
      <input
        type="password"
        autocomplete="current-password"
        bind:value={password}
        placeholder="Passord"
        aria-label="Passord"
        required
        minlength="12"
        style="flex: 0 1 14rem; min-width: 0;"
      />
      <button class="primary" type="submit" disabled={busy}>
        {busy ? 'Låser opp …' : 'Lås opp'}
      </button>
    </form>
    {#if error}
      <p class="error" role="alert" style="margin: 0.4rem 0 0;">{error}</p>
    {/if}
  </aside>
{/if}
